// S2-CC20.6b.1.5: FilterChip — toggleable chip for status/provider filter bars.
// Two exports:
//   - FilterChipGroup: the "Status"/"Provider" labelled wrapper (pill row).
//   - FilterChip: a single toggleable chip, optionally with a count badge.
//
// Wireframe: docs/sprint-2/wireframes/s2-cc6-llm-control-center.html
//   (.chip-group, .chip-group-label, .chip, .chip.active, .chip-count)
import type { ReactNode } from 'react';
import './FilterChip.css';

// ------------------------------------------------------------------
// FilterChip — the individual toggleable pill.
// ------------------------------------------------------------------
export interface FilterChipProps {
  label: string;
  selected: boolean;
  // The chip is fully controlled — parent owns state and toggles it on click.
  // Using onToggle (not onChange) matches the toggle semantics: one click ↔
  // one state flip; we don't pass a new value because the parent already
  // knows what the new value should be.
  onToggle: () => void;
  // Optional right-aligned count badge. Undefined = no badge. 0 is a valid
  // count that still renders (empty-but-known is a meaningful state).
  count?: number;
}

export function FilterChip({ label, selected, onToggle, count }: FilterChipProps) {
  return (
    <button
      type="button"
      // aria-pressed communicates the toggle state to assistive tech, which
      // is more accurate than role=checkbox for filter chips. See WAI-ARIA
      // Authoring Practices: "toggle button" pattern.
      aria-pressed={selected}
      onClick={onToggle}
      className={[
        'airgent-chip',
        selected ? 'airgent-chip-active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label}
      {/* Count is explicitly not rendered when undefined. count === 0 still
          renders — an operator seeing "Errors 0" gets useful info. */}
      {count !== undefined && <span className="airgent-chip-count">{count}</span>}
    </button>
  );
}

// ------------------------------------------------------------------
// FilterChipGroup — label + chip row wrapped in a surface-2 pill.
// ------------------------------------------------------------------
export interface FilterChipGroupProps {
  label: string;
  children: ReactNode;
}

export function FilterChipGroup({ label, children }: FilterChipGroupProps) {
  return (
    <div className="airgent-chip-group">
      <span className="airgent-chip-group-label">{label}</span>
      {children}
    </div>
  );
}
